import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import env from '../../../environment';
import type { FeaturePageDataInterface } from './types';

export const exportIntegrations: FeaturePageDataInterface = {
  slug: 'export-integrations',
  navTitle: 'Export integrations',
  shortTitle: 'Exports',
  navDescription:
    'Push captures into Google Sheets, Airtable, CSV, or your own webhook — without rebuilding pipelines.',
  Icon: ArrowDownTrayIcon,
  headline: 'Send crawl data where your team',
  highlight: 'already works',
  blurb:
    'Structured captures only help if they land in the tools you already use. Connect Google Sheets or Airtable, download CSV, or POST batches to a webhook — same export record, same sync history.',
  quote: {
    text: 'We stopped pasting scrape dumps into Sheets. Captures sync into the spreadsheet the research pod already lives in.',
    author: 'Customer name',
    authorRole: 'Job title, Company',
  },
  image: {
    src: '/images/shots/export-integrations.png',
    alt: 'Stark Crawl export destinations — Google Sheets, Airtable, CSV, and webhook',
  },
  content: [
    {
      type: 'block',
      body: {
        heading: 'Destinations your research stack already uses',
        body: [
          'Connect Google Sheets or Airtable once for the organisation, then attach exports at project, segment, or workflow scope. Columns follow your project schema — including capture URL, category, and timestamps.',
          'Prefer a file or your own pipeline? Create a CSV export for download, or a webhook that receives signed JSON batches as new captures land.',
        ],
        large: true,
      },
    },
    {
      type: 'block',
      body: {
        heading: 'One export model, four ways out',
        body: [
          'Google Sheets and Airtable keep rows appending as crawls run. CSV builds a downloadable artifact you can share or load elsewhere. Webhooks POST capture batches with optional Bearer auth and HMAC signatures.',
          'Every destination is a first-class export: sync status, counts, and history live on the same Exports tab — not a one-off download that disappears.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'From capture to spreadsheet without glue code',
        body: [
          'No Zapier tax for the common cases. Analysts open the sheet or Airtable base; ops teams wire a webhook into the CRM or warehouse they already run.',
          'Sync when you need a catch-up, or let reconcile jobs keep destinations current as new captures arrive.',
        ],
      },
    },
    {
      type: 'solution',
      body: 'Connect Sheets or Airtable under Integrations, then add an export on any project — or ship CSV and webhooks with no OAuth required.',
      cta: 'Try for free',
      url: env.appUrl + '/register',
    },
  ],
  seo: {
    title: 'Export integrations - Stark Crawl',
    description:
      'Export crawl captures to Google Sheets, Airtable, CSV, or webhooks — structured fields from your project schema, with sync history on every destination.',
    keywords:
      'crawl export, Google Sheets export, Airtable integration, CSV download, webhook export, Stark Crawl',
  },
  ctaTitle: 'Get data out of the crawl',
  ctaBody:
    'Sheets, Airtable, CSV, or webhook — pick the destination your team already opens every morning.',
};
